import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import QueueService from './QueueService';
import './DineInPage.css';
import './OrdersCreatePage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:9001';

const CATALOG_EXPORT_URL = `${API_BASE}/business/catalog/menu/export/petpooja?business_id=2`;
const CREATE_ORDER_URL = `${API_BASE}/business/orders`;

const SLOTS = [
  { value: '11:00-15:00', label: '11 AM to 3 PM' },
  { value: '18:00-23:00', label: '6 PM to 11 PM' },
];

const getNextNDays = (n = 7) => {
  const dates = [];
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const todayDate = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(todayDate.getTime());
    d.setDate(todayDate.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const value = `${y}-${m}-${da}`;

    let label = '';
    if (i === 0) label = 'Today';
    else if (i === 1) label = 'Tomorrow';
    else label = `${daysOfWeek[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`;

    dates.push({ value, label });
  }
  return dates;
};

const getAvailableSlots = (biz) => {
  const metadata = biz?.business_metadata;
  if (!metadata) return [];
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return [];
  const methods = schedulingConfig.methods || {};
  const localDelivery = methods.local_delivery || {};
  if (!localDelivery.is_enabled) return [];
  const providers = localDelivery.providers || [];
  const slots = [];
  providers.forEach((prov) => {
    if (prov.slots) {
      prov.slots.forEach((s) => {
        slots.push({
          id: s.id,
          label: s.label,
          start: s.start,
          end: s.end,
          days_of_week: s.days_of_week || [],
          value: `${s.start}-${s.end}`
        });
      });
    }
  });
  return slots;
};

const isSlotActiveForDate = (slot, dateStr) => {
  if (!slot || !dateStr) return true;
  if (!slot.days_of_week || slot.days_of_week.length === 0) return true;

  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return true;
    const jsDay = d.getDay(); // 0 is Sunday, 1 is Monday, etc.
    const weekdayIndex = jsDay === 0 ? 6 : jsDay - 1; // 0 is Monday, 6 is Sunday
    return slot.days_of_week.includes(weekdayIndex);
  } catch (e) {
    return true;
  }
};

const getSupportedProviders = (biz, pincodeStr) => {
  const metadata = biz?.business_metadata;
  if (!metadata) return [];
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return [];
  const localDelivery = schedulingConfig.methods?.local_delivery;
  if (!localDelivery || !localDelivery.is_enabled) return [];
  const providers = localDelivery.providers || [];

  if (!pincodeStr || !pincodeStr.trim()) {
    return [];
  }
  const trimmedPin = pincodeStr.trim();
  return providers.filter(provider => {
    if (!provider.pincode_groups || provider.pincode_groups.length === 0) {
      return true;
    }
    return provider.pincode_groups.some(group =>
      group.pincodes && group.pincodes.includes(trimmedPin)
    );
  });
};

const calculateProviderDeliveryPrice = (provider, pincodeStr) => {
  if (!provider) return 0;
  const handlingFee = provider.handling_fee ?? 0;
  let shippingFee = 0;
  if (pincodeStr && provider.pincode_groups) {
    const trimmedPin = pincodeStr.trim();
    for (const group of provider.pincode_groups) {
      if (group.pincodes && group.pincodes.includes(trimmedPin)) {
        shippingFee = group.shipping_fee ?? 0;
        break;
      }
    }
  }
  return handlingFee + shippingFee;
};

const getProviderSlots = (provider) => {
  if (!provider || !provider.slots) return [];
  return provider.slots.map(s => ({
    id: s.id,
    label: s.label,
    start: s.start,
    end: s.end,
    days_of_week: s.days_of_week || [],
    value: `${s.start}-${s.end}`
  }));
};

const getPickupSlots = (biz, pickupLocationId) => {
  const metadata = biz?.business_metadata;
  if (!metadata) return [];
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return [];
  const pickup = schedulingConfig.methods?.pickup;
  if (!pickup || !pickup.is_enabled) return [];
  if (!pickupLocationId) return [];
  const loc = pickup.locations?.find(l => l.id === pickupLocationId);
  if (!loc || !loc.slots) return [];
  return loc.slots.map(s => ({
    id: s.id,
    label: s.label,
    start: s.start,
    end: s.end,
    days_of_week: s.days_of_week || [],
    value: `${s.start}-${s.end}`
  }));
};

const calculateDeliveryPrice = (biz, pincodeStr, orderType = 'HOME_DELIVERY') => {
  if (orderType === 'PICKUP') return 0;
  const metadata = biz?.business_metadata;
  if (!metadata) return 40; // Default fallback
  const schedulingConfig = metadata.order_rules?.scheduling_config || metadata.scheduling_config;
  if (!schedulingConfig || !schedulingConfig.is_enabled) return 40;

  const localDelivery = schedulingConfig.methods?.local_delivery;
  if (!localDelivery || !localDelivery.is_enabled) return 0;

  const provider = localDelivery.providers?.[0];
  if (!provider) return 0;

  const handlingFee = provider.handling_fee ?? 0;
  let shippingFee = 0;
  if (pincodeStr && provider.pincode_groups) {
    for (const group of provider.pincode_groups) {
      if (group.pincodes && group.pincodes.includes(pincodeStr.trim())) {
        shippingFee = group.shipping_fee ?? 0;
        break;
      }
    }
  }
  return handlingFee + shippingFee;
};

const isActive = (v) => String(v) === '1';
const rupee = (v) => `₹${parseFloat(v || 0).toFixed(0)}`;
const makeCartKey = (item, variation = null) => (variation ? `${item.itemid}_var_${variation.id}` : `${item.itemid}_simple`);
const buildRef = () => `ORD-${Date.now()}`;
const today = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};

const parseMenuResponse = (rawData) => {
  let data = rawData;
  if (rawData?.success === true && rawData?.data) {
    data = typeof rawData.data === 'string' ? JSON.parse(rawData.data) : rawData.data;
    if (data.response && typeof data.response === 'string') data = JSON.parse(data.response);
    else if (data.response && typeof data.response === 'object') data = data.response;
  }
  return data;
};

const normalizeMenu = (data) => ({
  ...data,
  categories: (data?.categories || []).map((c) => ({ ...c, categoryid: String(c.categoryid ?? c.id ?? ''), categoryname: c.categoryname ?? c.name ?? 'Category' })),
  items: (data?.items || []).map((i) => ({
    ...i,
    itemid: String(i.itemid ?? i.id ?? ''),
    itemname: i.itemname ?? i.name ?? 'Item',
    itemdescription: i.itemdescription ?? i.description ?? '',
    item_categoryid: String(i.item_categoryid ?? i.categoryid ?? i.category_id ?? ''),
    item_image_url: i.item_image_url ?? i.image ?? '',
    price: i.price ?? 0,
    active: i.active ?? '1',
    itemallowvariation: i.itemallowvariation ?? (Array.isArray(i.variation) && i.variation.length > 0 ? '1' : '0'),
    variation: (i.variation || []).map((v) => ({ ...v, id: String(v.id ?? ''), name: v.name ?? 'Variant', price: v.price ?? 0, active: v.active ?? '1', addon: v.addon || [] })),
    addon: i.addon || [],
  })),
  addongroups: data?.addongroups || [],
});

function ItemDrawer({ item, addongroups, onClose, onAddToCart }) {
  const hasVariations = String(item.itemallowvariation) === '1' && item.variation?.length > 0;
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [note, setNote] = useState('');
  const [qty, setQty] = useState(1);

  const applicableAddonGroupIds = useCallback(() => {
    if (hasVariations && selectedVariation) return (selectedVariation.addon || []).map((a) => a.addon_group_id);
    return (item.addon || []).map((a) => a.addon_group_id);
  }, [hasVariations, selectedVariation, item.addon]);

  const applicableGroups = addongroups.filter((g) => applicableAddonGroupIds().includes(g.addongroupid));
  const isAddonSelected = (g, a) => selectedAddons.some((x) => x.group_id === g && x.id === a);
  const basePrice = hasVariations && selectedVariation ? parseFloat(selectedVariation.price || 0) : parseFloat(item.price || 0);
  const addonTotal = selectedAddons.reduce((s, a) => s + parseFloat(a.price || 0), 0);
  const total = (basePrice + addonTotal) * qty;

  const toggleAddon = (groupId, groupName, ai) => {
    const key = `${groupId}_${ai.addonitemid}`;
    const next = selectedAddons.filter((a) => `${a.group_id}_${a.id}` !== key);
    if (next.length === selectedAddons.length) {
      next.push({ group_id: groupId, group_name: groupName, id: ai.addonitemid, name: ai.addonitem_name, price: ai.addonitem_price, quantity: 1 });
    }
    setSelectedAddons(next);
  };

  return (
    <>
      <div className="di-overlay" onClick={onClose} />
      <div className="di-drawer" role="dialog" aria-modal="true">
        <div className="di-drawer-handle" />
        <div className="di-drawer-head">
          <h2>{item.itemname}</h2>
          <button className="di-drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="di-drawer-body">
          {hasVariations && <><div className="di-label-pill">Choose Size / Variant</div><div className="di-variations-grid">{item.variation.filter((v) => isActive(v.active)).map((v) => <div key={v.id} className={`di-var-card ${selectedVariation?.id === v.id ? 'selected' : ''}`} onClick={() => { setSelectedVariation(v); setSelectedAddons([]); }}><span className="di-var-card-name">{v.name}</span><span className="di-var-card-price">{rupee(v.price)}</span></div>)}</div></>}
          {applicableGroups.length > 0 && <><div className="di-label-pill" style={{ marginTop: 20 }}>Customise</div>{applicableGroups.map((g) => <div key={g.addongroupid} className="di-addon-group"><div className="di-addon-group-name">{g.addongroup_name}</div>{g.addongroupitems.filter((ai) => isActive(ai.active)).map((ai) => <div key={ai.addonitemid} className={`di-addon-item ${isAddonSelected(g.addongroupid, ai.addonitemid) ? 'selected' : ''}`} onClick={() => toggleAddon(g.addongroupid, g.addongroup_name, ai)}><div style={{ display: 'flex', alignItems: 'center', flex: 1 }}><span className="di-addon-item-name">{ai.addonitem_name}</span></div>{parseFloat(ai.addonitem_price) > 0 && <span className="di-addon-item-price">+{rupee(ai.addonitem_price)}</span>}</div>)}</div>)}</>}
          <div className="di-label-pill" style={{ marginTop: applicableGroups.length > 0 ? 8 : 20 }}>Special Instructions</div>
          <textarea className="di-special-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Less spicy, no onion..." />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}><span style={{ fontSize: 14, fontWeight: 600 }}>Quantity</span><div className="di-counter"><button onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button><span>{qty}</span><button onClick={() => setQty((q) => q + 1)}>+</button></div></div>
        </div>
        <div className="di-drawer-footer"><button className="di-btn-primary" disabled={hasVariations && !selectedVariation} onClick={() => { const key = makeCartKey(item, hasVariations ? selectedVariation : null); const itemName = hasVariations && selectedVariation ? `${item.itemname} (${selectedVariation.name})` : item.itemname; onAddToCart({ cartKey: key, itemId: String(hasVariations && selectedVariation ? selectedVariation.id : item.itemid), itemName, displayName: item.itemname, variationName: selectedVariation?.name || null, addonLabel: selectedAddons.map((a) => a.name).join(', '), addonItems: selectedAddons, note, qty, unitPrice: basePrice, addonPrice: addonTotal, totalPrice: total }); onClose(); }}>{hasVariations && !selectedVariation ? 'Select a variant to continue' : `Add to Order · ${rupee(total)}`}</button></div>
      </div>
    </>
  );
}

function CartDrawer({ cart, onClose, onAction, onPlaceOrder }) {
  const cartItems = Object.values(cart);
  const subtotal = cartItems.reduce((s, i) => s + i.totalPrice, 0);
  return (
    <>
      <div className="di-overlay" onClick={onClose} />
      <div className="di-drawer" role="dialog" aria-modal="true">
        <div className="di-drawer-handle" />
        <div className="di-drawer-head"><h2>Your Order</h2><button className="di-drawer-close" onClick={onClose} aria-label="Close">✕</button></div>
        <div className="di-drawer-body">{cartItems.map((ci) => <div key={ci.cartKey} className="di-cart-item-row"><div className="di-cart-item-info"><div className="di-cart-item-name">{ci.displayName}</div><div className="di-cart-item-sub">{ci.variationName && <span>{ci.variationName} · </span>}{ci.addonLabel && <span>{ci.addonLabel} · </span>}{ci.note && <span>"{ci.note}"</span>}</div></div><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div className="di-counter"><button onClick={() => onAction('decrement', ci)}>−</button><span>{ci.qty}</span><button onClick={() => onAction('increment', ci)}>+</button></div><div className="di-cart-item-price">{rupee(ci.totalPrice)}</div><button style={{ background: 'none', border: 'none', color: 'var(--di-muted)', cursor: 'pointer', fontSize: 20 }} onClick={() => onAction('remove', ci)}>×</button></div></div>)}</div>
        {cartItems.length > 0 && <div className="di-drawer-footer"><button className="di-btn-primary" onClick={onPlaceOrder}>Place Order · {rupee(subtotal)}</button></div>}
      </div>
    </>
  );
}

export default function OrdersCreatePage({ token }) {
  const navigate = useNavigate();
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCatId, setActiveCatId] = useState(null);
  const [itemDrawer, setItemDrawer] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [createCustomName, setCreateCustomName] = useState('');
  const [createCustomPrice, setCreateCustomPrice] = useState('');
  const [createCustomQty, setCreateCustomQty] = useState(1);
  const [businessInfo, setBusinessInfo] = useState(null);

  const [checkout, setCheckout] = useState({
    name: '',
    phone_number: '',
    orderType: '',
    delivery_date: '',
    delivery_slot: '',
    delivery_slot_id: '',
    address: '',
    pincode: '',
    deliveryProviderId: '',
    pickupLocationId: '',
    description: '',
    deliveryCharges: 0
  });

  const handleCheckoutPincodeChange = (pincodeVal) => {
    const supported = getSupportedProviders(businessInfo, pincodeVal);
    let providerId = '';
    let fee = 0;
    if (supported.length === 1) {
      providerId = supported[0].id;
      fee = calculateProviderDeliveryPrice(supported[0], pincodeVal);
    }
    setCheckout((prev) => ({
      ...prev,
      pincode: pincodeVal,
      deliveryProviderId: providerId,
      deliveryCharges: fee,
      delivery_slot: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));
  };

  const handleCheckoutOrderTypeChange = (typeVal) => {
    setCheckout((prev) => ({
      ...prev,
      orderType: typeVal,
      delivery_date: '',
      delivery_slot: '',
      delivery_slot_id: '',
      deliveryProviderId: '',
      pickupLocationId: '',
      deliveryCharges: 0
    }));
  };

  const handleCheckoutProviderChange = (providerId) => {
    const supported = getSupportedProviders(businessInfo, checkout.pincode);
    const provider = supported.find(p => p.id === providerId);
    const fee = provider ? calculateProviderDeliveryPrice(provider, checkout.pincode) : 0;
    setCheckout((prev) => ({
      ...prev,
      deliveryProviderId: providerId,
      deliveryCharges: fee,
      delivery_slot: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));
  };

  const handleCheckoutPickupLocationChange = (locationId) => {
    setCheckout((prev) => ({
      ...prev,
      pickupLocationId: locationId,
      delivery_slot: '',
      delivery_slot_id: '',
      delivery_date: ''
    }));
  };

  useEffect(() => {
    const fetchMenuAndBusinessInfo = async () => {
      try {
        setLoading(true);

        // Fetch menu
        const res = await fetch(CATALOG_EXPORT_URL, { headers: { Authorization: `Bearer ${token}` } });
        const raw = await res.json();
        const parsed = normalizeMenu(parseMenuResponse(raw));
        if (parsed.success === '1' || parsed.success === true || raw.success === true) {
          setMenuData(parsed);
          setActiveCatId((parsed.categories || []).find((c) => isActive(c.active))?.categoryid || parsed.categories?.[0]?.categoryid || null);
        } else setError(parsed.message || raw.message || 'Failed to load menu');

        // Fetch business info
        const businessId = localStorage.getItem('businessId') || '2';
        const bizRes = await QueueService.GetBusinessInfo(parseInt(businessId, 10));
        setBusinessInfo(bizRes);
      } catch (e) {
        setError('Failed to load menu or business details');
      } finally {
        setLoading(false);
      }
    };
    fetchMenuAndBusinessInfo();
  }, [token]);

  const categories = useMemo(() => (menuData?.categories || []).filter((c) => isActive(c.active) || !c.active), [menuData]);
  const addongroups = menuData?.addongroups || [];
  const itemsForCategory = (categoryId) => (menuData?.items || []).filter((it) => it.item_categoryid === categoryId && (isActive(it.active) || !it.active));
  const totalItems = Object.values(cart).reduce((s, i) => s + i.qty, 0);
  const total = Object.values(cart).reduce((s, i) => s + i.totalPrice, 0);

  const addToCart = (ci) => setCart((prev) => {
    const ex = prev[ci.cartKey];
    if (!ex) return { ...prev, [ci.cartKey]: ci };
    const qty = ex.qty + ci.qty;
    return { ...prev, [ci.cartKey]: { ...ex, qty, totalPrice: (ex.unitPrice + ex.addonPrice) * qty } };
  });

  const onCartAction = (action, ci) => setCart((prev) => {
    const ex = prev[ci.cartKey];
    if (!ex) return prev;
    if (action === 'remove') { const n = { ...prev }; delete n[ci.cartKey]; return n; }
    if (action === 'increment') { const q = ex.qty + 1; return { ...prev, [ci.cartKey]: { ...ex, qty: q, totalPrice: (ex.unitPrice + ex.addonPrice) * q } }; }
    if (action === 'decrement') {
      const q = ex.qty - 1;
      if (q <= 0) { const n = { ...prev }; delete n[ci.cartKey]; return n; }
      return { ...prev, [ci.cartKey]: { ...ex, qty: q, totalPrice: (ex.unitPrice + ex.addonPrice) * q } };
    }
    return prev;
  });

  const addCreateCustomItem = () => {
    if (!createCustomName) return alert('Please enter item name');
    const price = parseFloat(createCustomPrice) || 0;
    const qty = parseInt(createCustomQty, 10) || 1;
    const customId = `custom_${Date.now()}`;
    const newItem = {
      cartKey: `${customId}_simple`,
      itemId: customId,
      itemName: createCustomName,
      displayName: createCustomName,
      variationName: null,
      addonLabel: '',
      addonItems: [],
      note: '',
      qty: qty,
      unitPrice: price,
      addonPrice: 0,
      totalPrice: price * qty
    };

    setCart((prev) => {
      const ex = prev[newItem.cartKey];
      if (!ex) return { ...prev, [newItem.cartKey]: newItem };
      const qtyCombined = ex.qty + newItem.qty;
      return { ...prev, [newItem.cartKey]: { ...ex, qty: qtyCombined, totalPrice: (ex.unitPrice + ex.addonPrice) * qtyCombined } };
    });

    setCreateCustomName('');
    setCreateCustomPrice('');
    setCreateCustomQty(1);
    alert('Custom item added to cart!');
  };

  const submitOrder = async () => {
    if (!checkout.name || !checkout.phone_number) return alert('Please add customer name and phone number');
    if (!checkout.delivery_date || !checkout.delivery_slot) return alert('Please select delivery date and slot');
    if (checkout.orderType === 'HOME_DELIVERY' && !checkout.address.trim()) return alert('Please add delivery address');
    if (checkout.orderType === 'HOME_DELIVERY' && !checkout.pincode.trim()) return alert('Please add pin code');
    if (checkout.orderType === 'PICKUP' && !checkout.pickupLocationId) return alert('Please select pickup location');

    const [slotStart, slotEnd] = checkout.delivery_slot.split('-');

    const constructISODatetime = (dateStr, timeStr) => {
      if (!dateStr || !timeStr) return null;
      if (timeStr.includes('T')) return timeStr;
      return `${dateStr}T${timeStr.substring(0, 5)}:00+05:30`;
    };

    const deliveryFee = checkout.orderType === 'HOME_DELIVERY' ? (parseFloat(checkout.deliveryCharges) || 0) : 0;
    const finalTotal = total + deliveryFee;

    const selectedPickupLocation = checkout.orderType === 'PICKUP'
      ? (businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {})
        .locations?.find(l => l.id === checkout.pickupLocationId)
      : null;
    const addressText = selectedPickupLocation
      ? selectedPickupLocation.address
      : checkout.address || 'Pickup from store';

    const getSlotsForSelection = () => {
      if (checkout.orderType === 'HOME_DELIVERY') {
        if (!checkout.deliveryProviderId) return [];
        const schedulingConfig = businessInfo?.business_metadata?.order_rules?.scheduling_config || businessInfo?.business_metadata?.scheduling_config;
        const providers = schedulingConfig?.methods?.local_delivery?.providers || [];
        const provider = providers.find(p => p.id === checkout.deliveryProviderId);
        const slots = getProviderSlots(provider);
        return slots.length > 0 ? slots : SLOTS;
      } else if (checkout.orderType === 'PICKUP') {
        if (!checkout.pickupLocationId) return [];
        const slots = getPickupSlots(businessInfo, checkout.pickupLocationId);
        return slots.length > 0 ? slots : SLOTS;
      }
      return [];
    };

    const slots = getSlotsForSelection();
    const selectedSlot = slots.find(s => s.value === checkout.delivery_slot);

    const deliverySlotLabelVal = (() => {
      if (checkout.orderType === 'HOME_DELIVERY') {
        const supported = getSupportedProviders(businessInfo, checkout.pincode);
        const provider = supported.find(p => p.id === checkout.deliveryProviderId);
        return selectedSlot && provider ? `${provider.label}: ${selectedSlot.label}` : (selectedSlot ? selectedSlot.label : null);
      } else {
        const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
        const loc = pickup.locations?.find(l => l.id === checkout.pickupLocationId);
        return selectedSlot && loc ? `${loc.label}: ${selectedSlot.label}` : (selectedSlot ? selectedSlot.label : null);
      }
    })();

    const payload = {
      phone_number: checkout.phone_number,
      name: checkout.name,
      order_details: {
        business_id: parseInt(localStorage.getItem('businessId') || '2', 10),
        total_amount: parseFloat(finalTotal.toFixed(2)),
        currency: 'INR',
        reference_id: buildRef(),
        status: 'PENDING',
        source: 'WHATSAPP',
        text: 'Order created from frontend',
        description: checkout.description,
        product_items: Object.values(cart).map((i) => ({
          product_retailer_id: i.itemId,
          product_name: i.itemName,
          quantity: i.qty,
          item_price: i.unitPrice,
          currency: 'INR',
          variant_name: i.variationName || '',
          final_price: i.unitPrice,
          item_discount: 0,
          addon_items: i.addonItems || [],
          specialnotes: i.note || ''
        })),
        address: {
          title: checkout.orderType === 'HOME_DELIVERY' ? 'Home' : 'Pickup',
          description: addressText,
          name: checkout.name,
          address1: addressText,
          zip: checkout.pincode || '',
          delivery_provider_id: checkout.orderType === 'HOME_DELIVERY' ? (checkout.deliveryProviderId || null) : (checkout.pickupLocationId || null),
          pickup_location_id: checkout.orderType === 'PICKUP' ? (checkout.pickupLocationId || null) : null,
        },
        gst_percentage: 0,
        packing_charges: 0,
        delivery_charges: deliveryFee,
        service_charges: 0,
        order_type: checkout.orderType === 'PICKUP' ? 'PARCEL' : checkout.orderType,
        payment_type: 'ONLINE',
        enable_delivery: checkout.orderType === 'HOME_DELIVERY',
        delivery_by_business: true,
        delivery_date: checkout.delivery_date,
        delivery_provider_id: checkout.orderType === 'HOME_DELIVERY' ? (checkout.deliveryProviderId || null) : (checkout.pickupLocationId || null),
        delivery_slot_id: checkout.delivery_slot_id || null,
        delivery_slot_label: deliverySlotLabelVal,
        slot_start_time: constructISODatetime(checkout.delivery_date, slotStart),
        slot_end_time: constructISODatetime(checkout.delivery_date, slotEnd),
      },
    };
    try {
      setCreating(true);
      const res = await fetch(CREATE_ORDER_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Order creation failed');
      alert('Order created successfully');
      navigate('/orders');
    } catch (e) { alert(e.message || 'Order creation failed'); } finally { setCreating(false); }
  };

  if (checkoutOpen) {
    const deliveryFee = checkout.orderType === 'HOME_DELIVERY' ? (parseFloat(checkout.deliveryCharges) || 0) : 0;
    const grandTotal = total + deliveryFee;

    const getSlotsForSelection = () => {
      if (checkout.orderType === 'HOME_DELIVERY') {
        if (!checkout.deliveryProviderId) return [];
        const schedulingConfig = businessInfo?.business_metadata?.order_rules?.scheduling_config || businessInfo?.business_metadata?.scheduling_config;
        const providers = schedulingConfig?.methods?.local_delivery?.providers || [];
        const provider = providers.find(p => p.id === checkout.deliveryProviderId);
        const slots = getProviderSlots(provider);
        return slots.length > 0 ? slots : SLOTS;
      } else if (checkout.orderType === 'PICKUP') {
        if (!checkout.pickupLocationId) return [];
        const slots = getPickupSlots(businessInfo, checkout.pickupLocationId);
        return slots.length > 0 ? slots : SLOTS;
      }
      return [];
    };

    return (
      <div className="di-page">
        <header className="di-header"><div className="di-header-brand"><div className="di-header-logo">🍴</div><div className="di-header-info"><h1>Checkout</h1><p>Complete order details</p></div></div><button className="di-cart-fab" onClick={() => setCheckoutOpen(false)}>← Back</button></header>
        <main className="di-body oc-checkout-shell">
          <div className="oc-checkout-grid">
            <section className="oc-checkout-card">
              <h2 className="oc-checkout-title">Delivery Details</h2>
              <p className="oc-checkout-subtitle">Fill the details below to place this order.</p>

              <div className="oc-form-grid">
                <div className="oc-form-field">
                  <label className="oc-form-label">Customer Name</label>
                  <input className="oc-form-input" value={checkout.name} onChange={(e) => setCheckout((p) => ({ ...p, name: e.target.value }))} />
                </div>

                <div className="oc-form-field">
                  <label className="oc-form-label">Phone Number</label>
                  <input className="oc-form-input" placeholder="+919876543210" value={checkout.phone_number} onChange={(e) => setCheckout((p) => ({ ...p, phone_number: e.target.value }))} />
                </div>

                <div className="oc-form-field">
                  <label className="oc-form-label">Delivery Type</label>
                  <select className="oc-form-input" value={checkout.orderType} onChange={(e) => handleCheckoutOrderTypeChange(e.target.value)}>
                    <option value="">Select Delivery Type</option>
                    <option value="HOME_DELIVERY">Home Delivery</option>
                    <option value="PICKUP">Pickup</option>
                  </select>
                </div>

                {checkout.orderType === 'HOME_DELIVERY' && (
                  <>
                    <div className="oc-form-field oc-form-field-full">
                      <label className="oc-form-label">Address</label>
                      <textarea className="oc-form-input oc-form-textarea" value={checkout.address} onChange={(e) => setCheckout((p) => ({ ...p, address: e.target.value }))} placeholder="Enter delivery address" />
                    </div>

                    <div className="oc-form-field">
                      <label className="oc-form-label">Pin Code</label>
                      <input className="oc-form-input" value={checkout.pincode} onChange={(e) => handleCheckoutPincodeChange(e.target.value)} placeholder="e.g. 560001" />
                    </div>
                  </>
                )}

                {checkout.orderType === 'PICKUP' && (() => {
                  const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
                  const locations = pickup.locations || [];
                  return (
                    <div className="oc-form-field">
                      <label className="oc-form-label">Pickup Location</label>
                      <select className="oc-form-input" value={checkout.pickupLocationId} onChange={(e) => handleCheckoutPickupLocationChange(e.target.value)}>
                        <option value="">Select Location</option>
                        {locations.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                      </select>
                    </div>
                  );
                })()}

                {checkout.orderType === 'HOME_DELIVERY' && checkout.pincode && (() => {
                  const supported = getSupportedProviders(businessInfo, checkout.pincode);
                  if (supported.length === 0) {
                    return (
                      <div className="oc-form-field oc-form-field-full" style={{ color: 'red', fontWeight: 'bold' }}>
                        No delivery providers available for this Pin Code.
                      </div>
                    );
                  }
                  if (supported.length > 1) {
                    return (
                      <div className="oc-form-field">
                        <label className="oc-form-label">Delivery Provider</label>
                        <select className="oc-form-input" value={checkout.deliveryProviderId} onChange={(e) => handleCheckoutProviderChange(e.target.value)}>
                          <option value="">Select Provider</option>
                          {supported.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                      </div>
                    );
                  }
                  return null;
                })()}

                {checkout.orderType === 'PICKUP' && checkout.pickupLocationId && (() => {
                  const pickup = businessInfo?.business_metadata?.order_rules?.scheduling_config?.methods?.pickup || businessInfo?.business_metadata?.scheduling_config?.methods?.pickup || {};
                  const loc = pickup.locations?.find(l => l.id === checkout.pickupLocationId);
                  return loc && (
                    <div className="oc-form-field oc-form-field-full" style={{ padding: '10px', borderRadius: '6px', fontSize: '0.9rem', border: '1px solid #374151' }}>
                      <strong>Store Address:</strong> {loc.address || 'Pickup from store'}
                    </div>
                  );
                })()}

                {((checkout.orderType === 'HOME_DELIVERY' && checkout.pincode && checkout.deliveryProviderId) ||
                  (checkout.orderType === 'PICKUP' && checkout.pickupLocationId)) && (
                    <div className="oc-form-field oc-form-field-full">
                      <label className="oc-form-label">Delivery Date</label>
                      <div className="date-chips-grid">
                        {getNextNDays(7).map((d) => (
                          <button
                            key={d.value}
                            type="button"
                            className={`chip-select ${checkout.delivery_date === d.value ? 'active' : ''}`}
                            onClick={() => setCheckout((p) => ({ ...p, delivery_date: d.value }))}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                {((checkout.orderType === 'HOME_DELIVERY' && checkout.pincode && checkout.deliveryProviderId && checkout.delivery_date) ||
                  (checkout.orderType === 'PICKUP' && checkout.pickupLocationId && checkout.delivery_date)) && (
                    <div className="oc-form-field oc-form-field-full">
                      <label className="oc-form-label">Delivery Slot</label>
                      <div className="slot-chips-grid">
                        {getSlotsForSelection().map((s) => {
                          const isActiveSlot = isSlotActiveForDate(s, checkout.delivery_date);
                          return (
                            <button
                              key={s.value}
                              type="button"
                              className={`chip-select ${checkout.delivery_slot === s.value ? 'active' : ''}`}
                              onClick={() => setCheckout((p) => ({ ...p, delivery_slot: s.value, delivery_slot_id: s.id || '' }))}
                              disabled={!isActiveSlot}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {checkout.delivery_slot && (
                  <>
                    {checkout.orderType === 'HOME_DELIVERY' && (
                      <div className="oc-form-field">
                        <label className="oc-form-label">Delivery Charges (₹)</label>
                        <input type="number" className="oc-form-input" value={checkout.deliveryCharges} onChange={(e) => setCheckout((p) => ({ ...p, deliveryCharges: parseFloat(e.target.value) || 0 }))} placeholder="e.g. 40" />
                      </div>
                    )}

                    <div className="oc-form-field oc-form-field-full">
                      <label className="oc-form-label">Remark / Notes</label>
                      <textarea className="oc-form-input oc-form-textarea" value={checkout.description} onChange={(e) => setCheckout((p) => ({ ...p, description: e.target.value }))} placeholder="Enter order remarks" />
                    </div>
                  </>
                )}
              </div>
            </section>

            <aside className="oc-summary-card">
              <div className="oc-summary-top">Order Summary</div>
              <div className="oc-summary-row">
                <span>Total Items</span>
                <strong>{totalItems}</strong>
              </div>
              <div className="oc-summary-row">
                <span>Items Subtotal</span>
                <strong>{rupee(total)}</strong>
              </div>
              <div className="oc-summary-row">
                <span>Delivery Charge</span>
                <strong>{deliveryFee > 0 ? rupee(deliveryFee) : 'Free'}</strong>
              </div>
              <div className="oc-summary-row" style={{ borderTop: '1px solid #E2E8F0', paddingTop: '8px', marginTop: '4px' }}>
                <span>Grand Total</span>
                <strong>{rupee(grandTotal)}</strong>
              </div>
              <button className="di-btn-primary oc-submit-btn" onClick={submitOrder} disabled={creating || !checkout.delivery_slot}>
                {creating ? 'Creating Order...' : `Create Order · ${rupee(grandTotal)}`}
              </button>
            </aside>
          </div>
        </main>
      </div>
    );
  }

  if (loading) return <div className="di-page"><div className="di-loading-state"><div className="di-spinner" /><p className="di-loading-text">Loading menu…</p></div></div>;
  if (error) return <div className="di-page"><div className="di-error-state"><div className="di-error-icon">🍽️</div><h2 style={{ margin: 0, fontSize: 20 }}>Menu unavailable</h2><p className="di-error-msg">{error}</p></div></div>;

  return (
    <div className="di-page">
      <header className="di-header">
        <div className="di-header-brand"><div className="di-header-logo">🍴</div><div className="di-header-info"><h1>Create Order</h1><p>Same dine-in ordering experience</p></div></div>
        <button className="di-cart-fab" onClick={() => setCartOpen(true)} style={{ opacity: totalItems === 0 ? 0.6 : 1 }}>🛒{totalItems > 0 && <div className="di-cart-badge">{totalItems}</div>}{totalItems > 0 && <span>View Cart</span>}</button>
      </header>
      {categories.length > 0 && <nav className="di-category-bar" aria-label="Menu categories">{categories.map((cat) => <button key={cat.categoryid} className={`di-cat-tab ${activeCatId === cat.categoryid ? 'active' : ''}`} onClick={() => setActiveCatId(cat.categoryid)}>{cat.categoryname}</button>)}</nav>}
      <main className="di-body">
        {categories.map((cat) => {
          const items = itemsForCategory(cat.categoryid);
          if (items.length === 0) return null;
          return <div key={cat.categoryid}><div className="di-section-title">{cat.categoryname}</div>{items.map((item) => <div key={item.itemid} className="di-item-card"><div className="di-item-thumb">{item.item_image_url ? <img src={item.item_image_url} alt={item.itemname} /> : '🍽️'}</div><div className="di-item-body"><div className="di-item-name">{item.itemname}</div>{item.itemdescription && <div className="di-item-desc">{item.itemdescription}</div>}<div className="di-item-footer"><div className="di-item-price"><span className="di-currency">₹</span>{String(item.itemallowvariation) === '1' ? (() => { const prices = (item.variation || []).filter((v) => isActive(v.active)).map((v) => parseFloat(v.price || 0)); if (prices.length === 0) return '0'; const min = Math.min(...prices); const max = Math.max(...prices); return min === max ? min.toFixed(0) : `${min.toFixed(0)}–${max.toFixed(0)}`; })() : parseFloat(item.price || 0).toFixed(0)}</div><button className="di-add-btn" onClick={() => setItemDrawer(item)}>+ Add</button></div></div></div>)}</div>;
        })}

        <div className="custom-item-section">
          <div className="custom-item-section-title">➕ Add Custom Item (Not in Menu)</div>
          <div className="custom-item-inputs">
            <div className="oc-form-field">
              <label className="oc-form-label">Item Name</label>
              <input className="oc-form-input" value={createCustomName} onChange={e => setCreateCustomName(e.target.value)} placeholder="e.g. Garlic Bread" />
            </div>
            <div className="oc-form-field">
              <label className="oc-form-label">Price (₹)</label>
              <input type="number" className="oc-form-input" value={createCustomPrice} onChange={e => setCreateCustomPrice(e.target.value)} placeholder="150" />
            </div>
            <div className="oc-form-field">
              <label className="oc-form-label">Qty</label>
              <input type="number" className="oc-form-input" value={createCustomQty} onChange={e => setCreateCustomQty(parseInt(e.target.value, 10) || 1)} />
            </div>
            <button type="button" className="di-btn-primary" style={{ marginTop: 0, height: '42px' }} onClick={addCreateCustomItem}>+ Add to Cart</button>
          </div>
        </div>

        <div style={{ height: 80 }} />
      </main>
      {itemDrawer && <ItemDrawer item={itemDrawer} addongroups={addongroups} onClose={() => setItemDrawer(null)} onAddToCart={addToCart} />}
      {cartOpen && <CartDrawer cart={cart} onClose={() => setCartOpen(false)} onAction={onCartAction} onPlaceOrder={() => { setCartOpen(false); setCheckoutOpen(true); }} />}
    </div>
  );
}
